import isNumber from "is-number";
import { IReactionDisposer, isObservableArray, when } from "mobx";

let idCount = 0;
export const idCounter = () => ++idCount;

export function decodeHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function isScrolledIntoView(el) {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;

  // Only completely visible elements return true:
  let isVisible = elemTop >= 0 && elemBottom <= window.innerHeight;
  // Partially visible elements return true:
  isVisible = isVisible || (elemTop < window.innerHeight && elemBottom >= 0);
  return isVisible;
}

export function guid(): string {
  return uuidv4(undefined).replace(/-/g, "");
}

//  @ts-ignore
// prettier-ignore
function uuidv4(a){return a?(a^Math.random()*16>>a/4).toString(16):([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,uuidv4)}

export const concatClassnames: (classNames: Array<any>) => string = (
  classNames
) =>
  classNames
    .reduce((acc, className) => {
      if (isArrayOrObservableArray(className)) {
        className = concatClassnames(className);
      }

      return acc + " " + (className ? className : "");
    }, "")
    .trim();

export const wait = (millisecondsToWait) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(undefined);
    }, millisecondsToWait);
  });
};

export const capitalizeFirstWord = (words) => {
  return words
    .split(" ")
    .map((word, index) => {
      if (index === 0) {
        return word.replace(/(^|\s)\S/g, (l) => l.toUpperCase());
      }

      return word;
    })
    .join(" ");
};

export const isArrayOrObservableArray = (x) => {
  return Array.isArray(x) || isObservableArray(x);
};

export const firstDefined = (values: any[]) =>
  values.find((v) => v !== undefined);

function hashFnv32a(str, asString, seed) {
  /*jshint bitwise:false */
  let i,
    l,
    hval = seed === undefined ? 0x811c9dc5 : seed;

  for (i = 0, l = str.length; i < l; i++) {
    hval ^= str.charCodeAt(i);
    hval +=
      (hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24);
  }
  if (asString) {
    // Convert to 8 digit hex string
    return ("0000000" + (hval >>> 0).toString(16)).substr(-8);
  }
  return hval >>> 0;
}

export function hashCode(string): string {
  return hashFnv32a(string, true, "12345").toString();
}

export function modulo(n, m) {
  return ((n % m) + m) % m;
}

export const removeUndefined = (obj: Object) =>
  Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    if (val !== undefined) {
      acc[key] = val;
    }
    return acc;
  }, {});

export const removeNull = (obj: Object) =>
  Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    if (val !== null) {
      acc[key] = val;
    }
    return acc;
  }, {});

export const removeNullAndUndefined = (obj: Object) =>
  Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    if (val !== null && val !== undefined) {
      acc[key] = val;
    }
    return acc;
  }, {});

export const removeRecursive = (obj: Object, remove: (val) => boolean) => {
  const result = Object.keys(obj).reduce((acc, key) => {
    let val = obj[key];

    if (typeof val === "object" && val) {
      val = removeRecursive(val, remove);
    }

    if (!remove(val)) {
      acc[key] = val;
    }
    return acc;
  }, {});

  if (Object.keys(result).length === 0) return null;

  return result;
};

export const hexToRgba = (hex: string, a: number) => {
  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const onKeyDown = (key: string, func: Function) => {
  return {
    onKeyDown: (evt) => {
      if (evt.key === key) func();
    },
  };
};

/** Adapted from C# FieldFormatter.FormatPhoneNumberDisplay */
export const formatPhone = (phone: string) => {
  let cleaned = phone.replace(/\s/g, "").replace(/^\+/, "00");
  const canFormat =
    isNumber(cleaned) && cleaned.length >= 8 && cleaned.length <= 13;
  if (!canFormat) return phone.trim();

  let lasteight = cleaned.substr(cleaned.length - 8);
  let front = cleaned.substr(0, cleaned.length - 8);

  if (front.startsWith("00") && front.length > 2) {
    front = `+ ${front.substr(2)}`;
  }

  const isMobile = lasteight.startsWith("4") || lasteight.startsWith("9");

  if (isMobile) {
    const a = lasteight.slice(0, 3);
    const b = lasteight.slice(3, 5);
    const c = lasteight.slice(5, 8);

    return `${front} ${a} ${b} ${c}`.trim();
  }

  const a = lasteight.slice(0, 2);
  const b = lasteight.slice(2, 4);
  const c = lasteight.slice(4, 6);
  const d = lasteight.slice(6, 8);

  return `${front} ${a} ${b} ${c} ${d}`.trim();
};

const defaultFormatter = new Intl.NumberFormat("no-NO", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const formatNumber = (
  num: string | number,
  formatter = defaultFormatter
) => {
  if (num === null || num === undefined) return "";
  if (typeof num === "string") num = Number(num);

  return formatter.format(num);
};

export const awaitableWhen = (
  f: () => any,
  onWaiting?: (disposer: IReactionDisposer) => any
) =>
  new Promise((r) => {
    const disposer = when(f, () => r(null));
    if (onWaiting) onWaiting(disposer);
    return disposer;
  });

/**
 * Stopping propagation seems to only work in
 * development mode, and not in production mode.
 *
 * Calling evt.persist seems to fix it in production.
 */
export const persistAndStopPropagation = (evt: any) => {
  if (typeof evt.persist === "function") evt.persist();
  if (typeof evt.stopPropagation === "function") evt.stopPropagation();
};

/** Convert PascalCase to camelCase */
export function toCamelCase(o) {
  let newO, origKey, newKey, value;
  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === "object") {
        value = toCamelCase(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey)) {
        newKey = (
          origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey
        ).toString();
        value = o[origKey];
        if (
          value instanceof Array ||
          (value !== null && value.constructor === Object)
        ) {
          value = toCamelCase(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
}

export const splitUnit = (v: string) => {
  if (typeof v === "string" && v !== "") {
    const split = v.match(/^([-.\d]+(?:\.\d+)?)(.*)$/);
    return [split[1].trim(), split[2].trim()];
  } else {
    return [v, ""];
  }
};

export const findSelfOrParent = (node, f) => {
  if (!node) return null;
  try {
    if (f(node)) return node;
  } catch (e) {}

  if (!node.parentNode) return null;
  return findSelfOrParent(node.parentNode, f);
};
